import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { Settings } from "../../../settings";
import { selectBet } from "../../../State/selectors";
import OverlaySymbol from "../OverlaySymbol/OverlaySymbol";
import OverlayReelController from "./OverlayReelController";
import { safeInit } from "../../../utilities";

const OverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, overlaySymbols }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const overlaySymbolRefs = useRef(new Array(Settings.SYMBOLS_VISIBLE));
    const reelContainerRef = useRef(null);
    const bet = useSelector(selectBet);

    // ✅ Initialize controller once when component mounts
    useEffect(() => {
      if (!overlayReelControllerRef.current && reelContainerRef.current) {
        safeInit(() => {
          overlayReelControllerRef.current = new OverlayReelController(
            overlaySymbolRefs.current,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current,
            bet
          );
        });
      }

      return () => {
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ Handle resize of all overlay symbols
    useEffect(() => {
      if (overlayReelControllerRef.current) {
        overlayReelControllerRef.current.resizeAnimations(
          symbolWidth,
          symbolHeight
        );
      }
    }, [symbolHeight, symbolWidth]);

    // 💰 Bet update - update coin values when bet changes
    useEffect(() => {
      if (!overlayReelControllerRef.current) return;
      overlayReelControllerRef.current.updateBet(bet);
    }, [bet]);

    // ✅ Expose all imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getOverlayReelController: () => overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showAppearAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimation(
          symbolIndex,
          symbolId
        );
      },

      showAppearAnimationAtPosition: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimationAtPosition(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      replaceVisibleSymbols: (targetSymbolId) => {
        overlayReelControllerRef.current?.replaceVisibleSymbols(targetSymbolId);
      },

      playExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReel();
      },

      playExpandingWildFullReelWin: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReelWin();
      },

      hideExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.hideExpandingWildFullReel();
      },

      forceStopExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.forceStopExpandingWildFullReel();
      },
    }));

    return (
      <Container
        ref={reelContainerRef}
        name={`spine_overlay_reel_${index}`}
        x={symbolWidth * (index - 2)}
      >
        {overlaySymbols.map((_, idx) => (
          <OverlaySymbol
            key={idx}
            index={idx}
            y={symbolHeight * idx}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              overlaySymbolRefs.current[idx] = el;
            }}
          />
        ))}
      </Container>
    );
  }
);

export default OverlayReel;
